import * as React from "react";
import { styled } from "styled-components";
import { Subheading, MultiSelectField } from "@wayflyer/flyui";
import { useIntl } from "react-intl";

import type { MetricReferenceDirection } from "../shared";
import { MetricCard } from "../metric-card/metric-card";
import { Spinner } from "../styled";

interface PinnedMetricsHeaderProps {
  metrics: PinnedMetric[];
  title: string;
  dropdownLabel?: string;
  dropdownSearchPlaceholder?: string;
}

export interface PinnedMetricsProps {
  metrics: PinnedMetric[];
  title: string;
  loading?: boolean;
  dropdownLabel?: string;
  dropdownSearchPlaceholder?: string;
}

export type PinnedMetric = {
  metricType: string;
  title: string;
  metricValue?: string;
  metricReferenceValue?: string;
  metricReferenceDirection?: MetricReferenceDirection;
  metricReferenceSuccess?: boolean;
  metricReferenceText?: string;
  tooltip?: string;
  enabled: boolean;
  onEnable: (enabled: boolean) => void;
};

const Container = styled.div``;
const MultiselectContainer = styled.div`
  display: flex;
  min-width: 80px;
  width: 200px;
`;
const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 20px;
`;

const CardsGrid = styled.div`
  display: flex;
  flex-flow: row wrap;
  row-gap: 10px;
  column-gap: 20px;
  justify-content: start;
  align-items: baseline;
`;

const PinnedMetricsHeader = ({
  metrics,
  title,
  dropdownLabel,
  dropdownSearchPlaceholder,
}: PinnedMetricsHeaderProps): UIElement => {
  const intl = useIntl();
  const msbLabel =
    dropdownLabel ??
    intl.formatMessage({ id: "Pinned metrics default message", defaultMessage: "Choose pinned metrics" });
  const msbSearchPlaceholder =
    dropdownSearchPlaceholder ??
    intl.formatMessage({ id: "Search pinned metrics default message", defaultMessage: "Search metrics.." });
  const msb = (
    <MultiselectContainer>
      <MultiSelectField
        name="pinned-metrics"
        placeholder={msbLabel}
        showSearch={true}
        searchPlaceholder={msbSearchPlaceholder}
        options={metrics.map((metric) => {
          return {
            id: metric.metricType,
            text: metric.title,
            selected: metric.enabled,
            tooltip: metric.tooltip,
            onChange: (metricType: string, selected: boolean) => {
              metric.onEnable(selected);
            },
          };
        })}
      />
    </MultiselectContainer>
  );

  return title ? (
    <Container>
      <TitleRow>
        <Subheading>{title}</Subheading>
        {msb}
      </TitleRow>
    </Container>
  ) : (
    <TitleRow> {msb} </TitleRow>
  );
};

/**
 * PinnedMetrics component
 */
export const PinnedMetrics = ({
  metrics,
  dropdownLabel,
  dropdownSearchPlaceholder,
  title = "",
  loading = false,
}: PinnedMetricsProps): UIElement => {
  if (!metrics.length) {
    return <Spinner />;
  }
  return (
    <Container>
      <PinnedMetricsHeader
        metrics={metrics}
        title={title}
        dropdownLabel={dropdownLabel}
        dropdownSearchPlaceholder={dropdownSearchPlaceholder}
      />
      <CardsGrid>
        {metrics.map((metric, index) => {
          if (!metric.enabled) {
            return null;
          }
          return (
            <MetricCard
              key={`metric_card_${metric.metricValue}_${index}`}
              title={metric.title}
              metricValue={metric.metricValue}
              metricReferenceValue={metric.metricReferenceValue}
              metricReferenceDirection={metric.metricReferenceDirection}
              metricReferenceSuccess={metric.metricReferenceSuccess}
              metricReferenceText={metric.metricReferenceText}
              tooltip={metric.tooltip}
              loading={loading}
            />
          );
        })}
      </CardsGrid>
    </Container>
  );
};
